import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ButtonIcon } from '@pkg/components/Sections/Profile/Buttons';
import { ReactComponent as BackIcon } from '@pkg/components/Sections/Profile/icons/icon-back.svg';
import { useCreateWishlist, useMoveToWishlist } from '@pkg/hooks';
import { fetchRandomName } from '@pkg/utils';

import { type YatPageItemNFTResponse } from '@app/client/common/structs/yatPage/yat-page-items';

import { WishlistThumbnail } from '../WishlistThumbnail';
import { StyledStar } from '../styles';

import { CreateWishlistContent } from './CreateWishlistContent';

import { BackButtonWrapper, Heading, ModalLayout, SectionWrapper, Subheading } from './styles';

interface CreateWishlistProps {
    onBack(): void;
    assetData?: Partial<YatPageItemNFTResponse>;
    slug?: string;
    thumbnail?: string;
    oldWishlistSlug?: string;
    setPopoverFullyOpen(val: boolean): void;
}

export const CreateWishlist = ({
    onBack,
    assetData,
    oldWishlistSlug,
    thumbnail,
    slug,
    setPopoverFullyOpen,
}: CreateWishlistProps) => {
    const { t: translate } = useTranslation(['nft_details']);
    const { mutateAsync: createWishlist, isLoading: isLoadingCreate } = useCreateWishlist();
    const [wishlistName, setWishlistName] = useState('');
    const [isLoadingGeneratedName, setIsLoadingGeneratedName] = useState(true);
    const [isPrivate, setIsPrivate] = useState(false);
    const { moveToWishlist, isLoading: isMoving } = useMoveToWishlist({
        contract: assetData?.contract_address,
        token_id: assetData?.token_id,
        slug,
    });

    useEffect(() => {
        fetchRandomName()
            .then((autoGeneratedName) => setWishlistName(autoGeneratedName))
            .finally(() => setIsLoadingGeneratedName(false));
    }, []);

    const handleCreateWishlist = () => {
        if (oldWishlistSlug) {
            createWishlist({ name: wishlistName, isPrivate })
                .then(
                    (newWishlist) =>
                        newWishlist?.ok &&
                        newWishlist.value &&
                        moveToWishlist({
                            oldWishlistSlug,
                            newWishlistSlug: newWishlist.value.slug,
                            newWishlistName: wishlistName,
                            callback: onBack,
                        }),
                )
                .catch(onBack);
        }
    };

    return (
        <ModalLayout>
            <BackButtonWrapper>
                <ButtonIcon color="translucent" size="smallMedium" variant="solid" onClick={onBack}>
                    <BackIcon />
                </ButtonIcon>
            </BackButtonWrapper>

            <SectionWrapper>
                {assetData?.image && <WishlistThumbnail assets={[assetData.image]} />}
                {thumbnail && !assetData && <WishlistThumbnail assets={[thumbnail]} />}
                {!thumbnail && (!assetData || !assetData.image) && <StyledStar forceSprite emojiID="⭐" />}
                <Heading>{translate('detail_view_wishlist_create_heading')}</Heading>
                <Subheading>{translate('detail_view_wishlist_create_subheading')}</Subheading>
            </SectionWrapper>

            <CreateWishlistContent
                disabled={isLoadingGeneratedName}
                handleCreate={handleCreateWishlist}
                setPopoverFullyOpen={setPopoverFullyOpen}
                setWishlistName={setWishlistName}
                wishlistName={wishlistName}
                setIsPrivate={setIsPrivate}
                isPrivate={isPrivate}
                isLoading={isMoving || isLoadingCreate}
            />
        </ModalLayout>
    );
};
